const LEGACY_FIREBASE_SW_PATH = '/firebase-messaging-sw.js';
const ROOT_SW_PATH = '/sw.js';
const APP_STORAGE_KEYS = [
  'accessToken',
  'authUser',
  'shop_credits_balance',
  'yubu_visitor_id',
  'yubu_session_id',
  'yubu_payment_debug_log',
  'yubu_last_search_context',
  'last_active_ts',
  'pwa_update_clicked_at',
];

export function getRegistrationScriptUrl(registration) {
  return (
    registration.active?.scriptURL ||
    registration.waiting?.scriptURL ||
    registration.installing?.scriptURL ||
    ''
  );
}

export async function cleanupLegacyRootFirebaseWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const rootScope = new URL('/', window.location.origin).href;
  const firebaseWorkerUrl = new URL(LEGACY_FIREBASE_SW_PATH, window.location.origin).href;

  let removed = false;

  await Promise.all(
    registrations.map(async (registration) => {
      const scriptUrl = getRegistrationScriptUrl(registration);
      if (registration.scope === rootScope && scriptUrl === firebaseWorkerUrl) {
        removed = true;
        await registration.unregister();
      }
    })
  );

  return removed;
}

export async function clearLegacyServiceWorkerCaches() {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return;
  }

  const cacheNames = await window.caches.keys();
  const staleCachePattern =
    /(workbox|precache|runtime|vite-plugin-pwa|yubumarket)/i;

  await Promise.all(
    cacheNames
      .filter((cacheName) => staleCachePattern.test(cacheName))
      .map((cacheName) => window.caches.delete(cacheName))
  );
}

export async function clearAppBrowsingData() {
  if (typeof window === 'undefined') {
    return { clearedLocalStorage: false, clearedSessionStorage: false, unregisteredWorkers: 0 };
  }

  let clearedLocalStorage = false;
  let clearedSessionStorage = false;
  let unregisteredWorkers = 0;

  try {
    APP_STORAGE_KEYS.forEach((key) => {
      window.localStorage.removeItem(key);
    });
    clearedLocalStorage = true;
  } catch {
    // ignore storage failures during recovery
  }

  try {
    APP_STORAGE_KEYS.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
    clearedSessionStorage = true;
  } catch {
    // ignore storage failures during recovery
  }

  await clearLegacyServiceWorkerCaches();

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(async (registration) => {
          await registration.unregister();
          unregisteredWorkers += 1;
        })
      );
    } catch {
      // ignore worker cleanup failures during recovery
    }
  }

  return { clearedLocalStorage, clearedSessionStorage, unregisteredWorkers };
}

export async function prepareServiceWorkerEnvironment() {
  const removedLegacyFirebaseWorker = await cleanupLegacyRootFirebaseWorker();

  if (removedLegacyFirebaseWorker) {
    await clearLegacyServiceWorkerCaches();
  }

  return {
    removedLegacyFirebaseWorker,
  };
}

export function isAppControlledByUnexpectedWorker() {
  if (typeof window === 'undefined' || !navigator.serviceWorker?.controller) {
    return false;
  }

  const scriptUrl = navigator.serviceWorker.controller.scriptURL || '';
  return (
    scriptUrl.includes(LEGACY_FIREBASE_SW_PATH) ||
    (!scriptUrl.includes(ROOT_SW_PATH) && !scriptUrl.includes(LEGACY_FIREBASE_SW_PATH))
  );
}
